import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {MetaTitle} from '../../../decorator/meta-title';
import {IsGrantedDirective} from '../../../directives/is-granted/is-granted.directive';
import {StatisticsService} from '../../../services/statistics/statistics.service';
import {HomeStat} from '../../../models/statistic/home-stat';

import {ChartOptions, ChartType} from 'chart.js';
import {BaseChartDirective} from 'ng2-charts';
import {MoneyPipe} from '../../../pipes/money/money.pipe';
import {ImageService} from '../../../services/image/image.service';
import {ColoredPercentPipe} from '../../../pipes/percent/colored-percent.pipe';
import {FormsModule} from '@angular/forms';
import {SimpleStat} from '../../../models/statistic/simple-stat';

type HomeSortKey = 'label' | 'cost' | 'revenue' | 'margin';

interface HomeTableSort {
  key: HomeSortKey;
  order: 'asc' | 'desc';
  expanded: boolean;
}

@Component({
  selector: 'app-home',
  imports: [
    IsGrantedDirective,
    BaseChartDirective,
    MoneyPipe,
    ColoredPercentPipe,
    FormsModule
],
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './home.component.scss'
})
@MetaTitle('Dashboard')
export class HomeComponent implements OnInit {
  private backgroundColor: string[] = [
    '#A8D5BA', '#FFD6A5', '#FFABAB', '#FFC3A0', '#D5AAFF',
    '#85E3FF', '#B9FBC0', '#FFCBC1', '#FFC6FF', '#D0F4DE'
  ];

  chartType: ChartType = 'pie';
  chartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {color: '#444'},
      }
    }
  };
  topAffiliateChart = {
    labels: [] as string[],
    datasets: [{
      data: [] as number[],
      backgroundColor: [] as string[],
    }]
  };
  topMediabuyerChart = {
    labels: [] as string[],
    datasets: [{
      data: [] as number[],
      backgroundColor: [] as string[],
    }]
  };

  statistics: HomeStat = new HomeStat();
  loading = true;

  readonly topLimit = 10;
  productSort: HomeTableSort = {key: 'cost', order: 'desc', expanded: false};
  siteSort: HomeTableSort = {key: 'cost', order: 'desc', expanded: false};

  constructor(
    protected imageService: ImageService,
    private statisticsService: StatisticsService
  ) {
  }

  ngOnInit(): void {
    this.refresh();
  }

  sortProducts(key: HomeSortKey): void {
    this.toggleSort(this.productSort, key);
    this.statistics.products = this.sortRows(this.statistics.products ?? [], this.productSort);
  }

  sortSites(key: HomeSortKey): void {
    this.toggleSort(this.siteSort, key);
    this.statistics.sites = this.sortRows(this.statistics.sites ?? [], this.siteSort);
  }

  getVisibleRows<T>(rows: T[], sort: HomeTableSort): T[] {
    return sort.expanded ? rows : rows.slice(0, this.topLimit);
  }

  getSortIcon(sort: HomeTableSort, key: HomeSortKey): string {
    if (sort.key !== key) {
      return '';
    }

    return sort.order === 'desc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill';
  }

  private toggleSort(sort: HomeTableSort, key: HomeSortKey): void {
    if (sort.key === key) {
      sort.order = sort.order === 'desc' ? 'asc' : 'desc';
      return;
    }

    sort.key = key;
    sort.order = key === 'label' ? 'asc' : 'desc';
  }

  private sortRows<T extends SimpleStat & {label: string}>(rows: T[], sort: HomeTableSort): T[] {
    const direction = sort.order === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      if (sort.key === 'label') {
        return a.label.localeCompare(b.label) * direction;
      }

      const av = sort.key === 'margin' ? a.getMarginPercent() : a[sort.key];
      const bv = sort.key === 'margin' ? b.getMarginPercent() : b[sort.key];

      return (av - bv) * direction;
    });
  }

  refresh(): void {
    this.statisticsService.getHomePartners().subscribe(partnerTypes => {
      this.statistics.partnerTypes = partnerTypes;
    });

    this.statisticsService.getHomeProducts().subscribe(products => {
      this.statistics.products = this.sortRows(products, this.productSort);
    });

    this.statisticsService.getHomeSites().subscribe(sites => {
      this.statistics.sites = this.sortRows(sites, this.siteSort);
    });

    this.statisticsService.getHomeTopAffiliates().subscribe(topAffiliates => {
      this.statistics.topAffiliates = topAffiliates;
        this.topAffiliateChart = {
          labels: this.statistics.topAffiliates.map(item => item.partnerCompanyName),
          datasets: [{
            data: this.statistics.topAffiliates.map(item => item.amount),
            backgroundColor: this.backgroundColor,
          }]
        };
    });

    this.statisticsService.getHomeTopMediabuyers().subscribe(topMediabuyers => {
      this.statistics.topMediabuyers = topMediabuyers;
        this.topMediabuyerChart = {
          labels: this.statistics.topMediabuyers.map(item => item.partnerCompanyName),
          datasets: [{
            data: this.statistics.topMediabuyers.map(item => item.amount),
            backgroundColor: this.backgroundColor,
          }]
        };
    });
  }
}
